import { inject, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { CatalogTitle, LandingPageComponent } from "../landing-page.model";
import { CatalogApiItem, CatalogsPageResponse, CatalogsViewModel, getCatalogComponents } from "./catalogs-view.model";
import { CacheService } from "../../common/services/cache-service";
import { CACHE_KEYS } from "../../common/constants/cache-keys";
import { eCatalog } from "../../common/constants/catalog";
import { UserBrrContext } from "../../common/models/brr-context";
import { VehicleSearchEasyaccessService } from "../vehicle-search-easyaccess/vehicle-search-easyaccess.service";


@Injectable({ providedIn: "root" })
export class CatalogsViewService {
  private readonly cacheService = inject(CacheService);
  private readonly vehicleSearchService = inject(VehicleSearchEasyaccessService);

  getCatalogs(resolvedPageJson: CatalogsPageResponse): Observable<CatalogsViewModel> {
    return of(this.toCatalogsViewModel(resolvedPageJson));
  }

  getCatalogInapplicability(): Record<string, boolean> {
    const applicability: Record<string, boolean> = {
      [eCatalog.StellTechDoc]: false,
      [eCatalog.Subscriptions]: false,
      [eCatalog.AccessoryCatalog]: false,
      [eCatalog.WorkshopProducts]: false,
      [eCatalog.SecondHandParts]: false,
      [eCatalog.ElectronicParts]: false,
      [eCatalog.AutomaticTransmission]: false,
      [eCatalog.Valorauto]: false,
      [eCatalog.SBMB]: false,
      [eCatalog.TireCatalog]: false,
      [eCatalog.TireBox]: false,
    };

    const brrContext = this.cacheService.getFromCache(CACHE_KEYS.USER.USER_BRR_CONTEXT) as UserBrrContext;
    if (brrContext) {
      applicability[eCatalog.SBMB] = !!brrContext.disableSBMB;
      applicability[eCatalog.TireCatalog] = !!brrContext.disableTirecatalog;
      applicability[eCatalog.TireBox] = !!brrContext.disableTireBox;
    }

    const vehicleContext = this.cacheService.getFromCache(CACHE_KEYS.USER.VEHICLE_CONTEXT);
    const hasBrandInfo = !!(vehicleContext?.brandCode || vehicleContext?.brandName || vehicleContext?.brandDescription);
    if (vehicleContext && hasBrandInfo && !this.vehicleSearchService.isBrandInternal(
      vehicleContext.brandCode,
      vehicleContext.brandName,
      vehicleContext.brandDescription,
    )) {
      [eCatalog.StellTechDoc, eCatalog.Subscriptions, eCatalog.SecondHandParts,
        eCatalog.ElectronicParts, eCatalog.AutomaticTransmission, eCatalog.Valorauto,
        eCatalog.TireBox].forEach(catalogId => applicability[catalogId] = true);
    }
    return applicability;
  }

  refreshCatalogInApplicability(featuredCatalogs: CatalogTitle[], catalogTiles: CatalogTitle[]): CatalogsViewModel {
    const applicability = this.getCatalogInapplicability();
    const applyToCatalogs = (catalogs: CatalogTitle[]) => catalogs
      .map(catalog => ({ ...catalog }))
      .filter(catalog => this.getCatalogStatus(catalog.catalogId, applicability) !== "hide")
      .map(catalog => ({ ...catalog, enabled: !applicability[catalog.catalogId] }));

    return {
      featuredCatalogs: applyToCatalogs(featuredCatalogs),
      catalogTiles: applyToCatalogs(catalogTiles),
    };
  }

  getVisibleAndDisabledStatusOfCatalog(catalogId: string, applicability: Record<string, boolean>): "enabled" | "disabled" | "hide" {
    return this.getCatalogStatus(catalogId, applicability);
  }

  private getCatalogStatus(catalogId: string, applicability: Record<string, boolean>): "enabled" | "disabled" | "hide" {
    if (catalogId === eCatalog.TireBox && applicability[eCatalog.TireBox]) return "hide";
    if (catalogId === eCatalog.TireCatalog) {
      if (!applicability[eCatalog.TireBox]) return "hide";
      if (applicability[eCatalog.TireCatalog]) return "disabled";
    }
    return applicability[catalogId] ? "disabled" : "enabled";
  }

  private toCatalogsViewModel(response: CatalogsPageResponse): CatalogsViewModel {
    const pageComponents: LandingPageComponent[] = getCatalogComponents(response);
    const catalogComponent = pageComponents.find(component => component.componentType === "catalog");
    const catalogs: CatalogApiItem[] = catalogComponent?.catalogs ?? [];
    const normalizedCatalogs = catalogs.map(catalog => this.normalizeCatalog(catalog));

    return {
      featuredCatalogs: normalizedCatalogs.filter(catalog => catalog.catalogPlacement === "primary"),
      catalogTiles: normalizedCatalogs.filter(catalog => catalog.catalogPlacement !== "primary"),
    };
  }

  private normalizeCatalog(catalog: CatalogApiItem): CatalogTitle {
    return {
      ...catalog,
      title: catalog.label?.trim() || catalog.catalogTitle?.trim() || catalog.title,
      description: catalog.catalogDescription ?? catalog.description ?? "",
      buttonLabel: catalog.ctaTitle?.trim() || catalog.buttonLabel || catalog.label,
      icon: catalog.icon,
      titleIcon: catalog.titleIcon,
      image: catalog.image || catalog.titleIcon || catalog.icon,
    };
  }
}
